/**
 * Ornament — el remate de esquina que aporta la piel activa.
 *
 * Hasta V2, CorpoCard dibujaba cuatro corchetes fijos sin importar el tema:
 * daba igual si estabas en Arasaka o en un tema victoriano, la esquina era
 * la misma. Ahora la forma sale del tema (theme.ornament), igual que el
 * color sale de los tokens.
 */

import { useTheme } from "../../hooks/useTheme";

type Corner = "tl" | "tr" | "bl" | "br";

const ROTATION: Record<Corner, string> = {
  tl: "rotate(0deg)",
  tr: "rotate(90deg)",
  br: "rotate(180deg)",
  bl: "rotate(270deg)",
};

const POSITION: Record<Corner, string> = {
  tl: "top-1.5 left-1.5",
  tr: "top-1.5 right-1.5",
  bl: "bottom-1.5 left-1.5",
  br: "bottom-1.5 right-1.5",
};

interface OrnamentProps {
  corner: Corner;
  size?: number;
  className?: string;
}

export function Ornament({ corner, size = 26, className = "" }: OrnamentProps) {
  const { theme } = useTheme();
  const kind = theme.ornament ?? "bracket";

  if (kind === "none") return null;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 26 26"
      fill="none"
      stroke="var(--bw-accent)"
      strokeWidth={1}
      aria-hidden="true"
      className={`absolute pointer-events-none ${POSITION[corner]} ${className}`}
      style={{ transform: ROTATION[corner], opacity: 0.6 }}
    >
      {kind === "filigree" ? (
        <>
          <path d="M1 12 Q1 1 12 1" />
          <path d="M1 18 Q1 5 14 5" opacity="0.5" />
          <circle cx="4" cy="4" r="1.4" fill="var(--bw-accent)" stroke="none" />
        </>
      ) : (
        <>
          <path d="M1 9 V1 H9" />
        </>
      )}
    </svg>
  );
}

/**
 * SectionRule — divisor de sección. Reemplaza el <hr> plano por algo que
 * pertenece al tema: sello con ojo en Niebla Gris, línea partida en el resto.
 */
export function SectionRule({ className = "" }: { className?: string }) {
  const { theme } = useTheme();
  const filigree = (theme.ornament ?? "bracket") === "filigree";

  return (
    <svg
      viewBox="0 0 600 22"
      width="100%"
      height={22}
      preserveAspectRatio="none"
      aria-hidden="true"
      className={`block ${className}`}
    >
      <line x1="0" y1="11" x2={filigree ? 242 : 280} y2="11" stroke="var(--bw-border)" strokeWidth={1} />
      <line x1={filigree ? 358 : 320} y1="11" x2="600" y2="11" stroke="var(--bw-border)" strokeWidth={1} />
      {filigree ? (
        <g stroke="var(--bw-accent)" fill="none" strokeWidth={1}>
          <path d="M258 11 q14 -8 22 0 q-14 8 -22 0Z" />
          <path d="M342 11 q-14 -8 -22 0 q14 8 22 0Z" />
          <circle cx="300" cy="11" r="5" />
          <circle cx="300" cy="11" r="1.8" fill="var(--bw-accent)" />
        </g>
      ) : (
        <g stroke="var(--bw-accent)" fill="none" strokeWidth={1}>
          <path d="M292 5 L300 11 L292 17" />
          <path d="M308 5 L300 11 L308 17" opacity="0.5" />
        </g>
      )}
    </svg>
  );
}
